import { useEffect, useState } from 'react';
import { openTickerChart } from '../tickerChart';
import { InfoTip } from './InfoTip';
import { PANEL_INFO } from '../panelInfo';

interface GoldData {
  updatedAt: string;
  spot: {
    usd: number;
    change24h: number | null;
    high24h: number | null;
    low24h: number | null;
    volume24h: number | null;
    intraday: number[];
  } | null;
  drivers: {
    realYield10y: number | null;
    realYieldPrev: number | null;
    dollarIndex: number | null;
    dollarPrev: number | null;
    asOf: string | null;
  } | null;
  vol: {
    gvz: number | null;
    gvzPrev: number | null;
    impliedDailyMovePct: number | null;
    asOf: string | null;
  } | null;
  paper: Array<{ ticker: string; changePercent: number | null; dollarVolume: number | null }>;
}

const chg = (v: number | null) =>
  v === null ? 'var(--text-dim)' : v >= 0 ? 'var(--green)' : 'var(--red)';
const pct = (v: number | null, d = 2) => (v === null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`);

function compact(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${(n / 1e3).toFixed(0)}K`;
}

function sparkPath(vals: number[], w = 100, h = 32): string {
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  return (
    'M' +
    vals
      .map((v, i) => {
        const x = (i / (vals.length - 1)) * w;
        const y = h - 2 - ((v - min) / span) * (h - 4);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' L')
  );
}

/* ---- Trading clock: gold's near-24h day, client-side ET math ---- */

function etParts(now: number): { day: number; mins: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(now));
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return {
    day: days.indexOf(get('weekday')),
    mins: (parseInt(get('hour'), 10) % 24) * 60 + parseInt(get('minute'), 10),
  };
}

interface ClockState {
  globexOpen: boolean;
  sessions: string[]; // active regional sessions
  nextLabel: string;
  nextInMins: number;
}

/**
 * COMEX Globex: Sun 18:00 ET → Fri 17:00 ET, daily halt 17:00–18:00 ET.
 * Regional flavor (approx): Asia 19:00–03:00, London 03:00–11:30, NY 08:20–13:30 ET.
 */
function goldClock(now: number): ClockState {
  const { day, mins } = etParts(now);
  const weekendClosed =
    day === 6 || (day === 5 && mins >= 17 * 60) || (day === 0 && mins < 18 * 60);
  const dailyHalt = !weekendClosed && mins >= 17 * 60 && mins < 18 * 60 && day >= 1 && day <= 4;
  const globexOpen = !weekendClosed && !dailyHalt;

  const sessions: string[] = [];
  if (globexOpen) {
    if (mins >= 19 * 60 || mins < 3 * 60) sessions.push('ASIA');
    if (mins >= 3 * 60 && mins < 11 * 60 + 30) sessions.push('LONDON');
    if (mins >= 8 * 60 + 20 && mins < 13 * 60 + 30) sessions.push('NEW YORK');
  }

  // Next notable event, in ET minutes-of-day terms (fixes converted: London
  // AM fix 10:30 / PM fix 15:00 London ≈ 05:30 / 10:00 ET most of the year).
  const events: Array<{ label: string; mins: number }> = [
    { label: 'London AM fix', mins: 5 * 60 + 30 },
    { label: 'COMEX floor open', mins: 8 * 60 + 20 },
    { label: 'London PM fix', mins: 10 * 60 },
    { label: 'COMEX floor close', mins: 13 * 60 + 30 },
    { label: 'Globex halt', mins: 17 * 60 },
    { label: 'Globex reopen', mins: 18 * 60 },
  ];
  if (weekendClosed) {
    // minutes until Sunday 18:00 ET
    const daysAhead = day === 6 ? 1 : 0; // Sat → Sun; Fri-eve and Sun handled below
    let until: number;
    if (day === 5) until = (24 * 60 - mins) + 24 * 60 + 18 * 60;
    else if (day === 6) until = (24 * 60 - mins) + 18 * 60 + (daysAhead - 1) * 0;
    else until = 18 * 60 - mins; // Sunday pre-open
    return { globexOpen, sessions, nextLabel: 'Globex reopen', nextInMins: until };
  }
  const upcoming =
    events.find((e) => e.mins > mins) ?? { label: events[0].label, mins: events[0].mins + 24 * 60 };
  return { globexOpen, sessions, nextLabel: upcoming.label, nextInMins: upcoming.mins - mins };
}

function fmtMins(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

export function GoldSection() {
  const [data, setData] = useState<GoldData | null>(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch('/api/gold');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
        setError(false);
      } catch {
        setError(true);
      }
    };
    load();
    const i = setInterval(load, 180000);
    const t = setInterval(() => setNow(Date.now()), 30000); // clock card granularity: minutes
    return () => {
      clearInterval(i);
      clearInterval(t);
    };
  }, []);

  if (error && !data) return null;
  const s = data?.spot ?? null;
  const d = data?.drivers ?? null;
  const v = data?.vol ?? null;
  const clock = goldClock(now);

  const rangePos =
    s && s.high24h !== null && s.low24h !== null && s.high24h > s.low24h
      ? ((s.usd - s.low24h) / (s.high24h - s.low24h)) * 100
      : null;

  return (
    <div className="panel bitcoin-section" id="gold">
      <div className="panel-header">
        <div className="panel-title">
          <h2>🥇 Gold</h2>
          <InfoTip paragraphs={PANEL_INFO.gold} />
        </div>
        <span className="badge">
          {clock.globexOpen
            ? clock.sessions.length > 0
              ? `${clock.sessions.join(' + ')} session`
              : 'Globex open'
            : 'market closed'}
        </span>
      </div>

      {!data ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="fed-grid">
          {/* Spot & intraday */}
          {s && (
            <div
              className="btc-card clickable-row"
              title="Click for XAUUSD chart"
              onClick={() => openTickerChart('XAUUSD')}
            >
              <h3 className="btc-card-title">Spot (per oz)</h3>
              <div className="btc-price">
                ${s.usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                <span style={{ color: chg(s.change24h), fontSize: 14, marginLeft: 8 }}>
                  {pct(s.change24h)}
                </span>
              </div>
              {s.intraday.length > 3 && (
                <svg className="btc-spark" viewBox="0 0 100 32" preserveAspectRatio="none">
                  <path
                    d={sparkPath(s.intraday)}
                    fill="none"
                    stroke={(s.change24h ?? 0) >= 0 ? 'var(--green-mid)' : 'var(--red)'}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}
              <div className="btc-rows btc-dim">
                <span>last 24h · via PAXG</span>
                <span>vol {s.volume24h !== null ? compact(s.volume24h) : '—'}</span>
              </div>
            </div>
          )}

          {/* Day range */}
          {s && s.low24h !== null && s.high24h !== null && (
            <div className="btc-card">
              <h3 className="btc-card-title">24h Range</h3>
              <div className="btc-rows">
                <span>
                  lo <b>${s.low24h.toLocaleString('en-US', { maximumFractionDigits: 0 })}</b>
                </span>
                <span>
                  hi <b>${s.high24h.toLocaleString('en-US', { maximumFractionDigits: 0 })}</b>
                </span>
              </div>
              {rangePos !== null && (
                <>
                  <div className="btc-fg-track" style={{ background: 'var(--green-faint)', opacity: 1 }}>
                    <div className="btc-fg-dot" style={{ left: `${rangePos}%` }} />
                  </div>
                  <div className="btc-rows btc-dim">
                    <span>
                      trading at {rangePos.toFixed(0)}% of the range —{' '}
                      {rangePos >= 75 ? 'near the high' : rangePos <= 25 ? 'near the low' : 'mid-range'}
                    </span>
                  </div>
                </>
              )}
              <div className="btc-rows btc-dim">
                <span>
                  width{' '}
                  {(((s.high24h - s.low24h) / s.low24h) * 100).toFixed(2)}% · $
                  {(s.high24h - s.low24h).toFixed(0)}
                </span>
              </div>
            </div>
          )}

          {/* Macro drivers */}
          {d && (
            <div className="btc-card">
              <h3 className="btc-card-title">Drivers</h3>
              <div className="btc-rows">
                <span>
                  10Y real yield{' '}
                  <b
                    style={{
                      // for gold, FALLING real yields are the green direction
                      color:
                        d.realYield10y !== null && d.realYieldPrev !== null
                          ? d.realYield10y < d.realYieldPrev
                            ? 'var(--green)'
                            : d.realYield10y > d.realYieldPrev
                              ? 'var(--red)'
                              : 'var(--text)'
                          : 'var(--text)',
                    }}
                  >
                    {d.realYield10y !== null ? `${d.realYield10y.toFixed(2)}%` : '—'}
                  </b>
                </span>
                <span className="btc-dim">
                  {d.realYield10y !== null && d.realYieldPrev !== null
                    ? d.realYield10y < d.realYieldPrev
                      ? 'falling → gold +'
                      : d.realYield10y > d.realYieldPrev
                        ? 'rising → gold −'
                        : 'flat'
                    : ''}
                </span>
              </div>
              <div className="btc-rows">
                <span>
                  dollar index{' '}
                  <b
                    style={{
                      color:
                        d.dollarIndex !== null && d.dollarPrev !== null
                          ? d.dollarIndex < d.dollarPrev
                            ? 'var(--green)'
                            : d.dollarIndex > d.dollarPrev
                              ? 'var(--red)'
                              : 'var(--text)'
                          : 'var(--text)',
                    }}
                  >
                    {d.dollarIndex !== null ? d.dollarIndex.toFixed(1) : '—'}
                  </b>
                </span>
                <span className="btc-dim">
                  {d.dollarIndex !== null && d.dollarPrev !== null
                    ? d.dollarIndex < d.dollarPrev
                      ? 'weaker → gold +'
                      : d.dollarIndex > d.dollarPrev
                        ? 'stronger → gold −'
                        : 'flat'
                    : ''}
                </span>
              </div>
              <div className="btc-rows btc-dim">
                <span>both move gold inversely · daily data (FRED)</span>
              </div>
            </div>
          )}

          {/* Volatility */}
          {v && v.gvz !== null && (
            <div className="btc-card">
              <h3 className="btc-card-title">Gold Volatility (GVZ)</h3>
              <div
                className="btc-price"
                style={{ color: v.gvz >= 20 ? 'var(--amber)' : 'var(--text)' }}
              >
                {v.gvz.toFixed(1)}
                {v.gvzPrev !== null && (
                  <span style={{ color: chg(v.gvz - v.gvzPrev), fontSize: 13, marginLeft: 8 }}>
                    {pct(((v.gvz - v.gvzPrev) / v.gvzPrev) * 100, 1)}
                  </span>
                )}
              </div>
              {v.impliedDailyMovePct !== null && (
                <div className="btc-rows">
                  <span>
                    implies ≈ <b>±{v.impliedDailyMovePct.toFixed(2)}%</b>
                    <span className="btc-dim"> daily move</span>
                  </span>
                </div>
              )}
              <div className="btc-rows btc-dim">
                <span>
                  {v.gvz >= 25 ? 'elevated — wide stops day' : v.gvz >= 16 ? 'normal regime' : 'calm regime'}
                </span>
              </div>
            </div>
          )}

          {/* Paper flow */}
          {data.paper.length > 0 && data.paper.some((p) => p.changePercent !== null) && (
            <div className="btc-card">
              <h3 className="btc-card-title">Paper Flow</h3>
              {data.paper.map((p) => (
                <div
                  className="btc-rows clickable-row"
                  key={p.ticker}
                  title={`${p.ticker} chart`}
                  onClick={() => openTickerChart(p.ticker)}
                  style={{ cursor: 'pointer' }}
                >
                  <span>
                    <b>{p.ticker}</b>
                    <span className="btc-dim">
                      {' '}
                      {p.ticker === 'GLD' ? 'gold ETF' : p.ticker === 'GDX' ? 'miners' : 'silver'}
                    </span>
                  </span>
                  <span>
                    <b style={{ color: chg(p.changePercent) }}>{pct(p.changePercent)}</b>
                    <span className="btc-dim">
                      {' '}
                      {p.dollarVolume !== null ? compact(p.dollarVolume) : ''}
                    </span>
                  </span>
                </div>
              ))}
              <div className="btc-rows btc-dim">
                <span>last session · GDX usually moves ~2–3× gold</span>
              </div>
            </div>
          )}

          {/* Trading clock */}
          <div className="btc-card">
            <h3 className="btc-card-title">Trading Clock</h3>
            <div
              className="btc-price"
              style={{ fontSize: 18, color: clock.globexOpen ? 'var(--green)' : 'var(--red)' }}
            >
              {clock.globexOpen
                ? clock.sessions.length > 0
                  ? clock.sessions.join(' + ')
                  : 'GLOBEX (quiet hours)'
                : 'CLOSED'}
            </div>
            <div className="btc-rows">
              <span>
                next: <b>{clock.nextLabel}</b>
              </span>
              <span className="btc-count" style={{ fontWeight: 700 }}>
                {fmtMins(clock.nextInMins)}
              </span>
            </div>
            <div className="btc-rows btc-dim">
              <span>
                gold trades ~23h/day Sun 18:00 – Fri 17:00 ET · liquidity peaks when LONDON + NEW
                YORK overlap
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
